import GameSignals from '@/signals/GameSignals'
import Facade from '@/Facade'

export class TournamentManager {
    private static _instance: TournamentManager
    private _tournamentId: number | null = null
    private _autoOpenHandled = false
    private _baseGameTransitionCompleted = false
    private _pendingBadgeAfterTransition = false

    private constructor() {
        this.init()
    }

    public static get instance(): TournamentManager {
        if (!TournamentManager._instance) {
            TournamentManager._instance = new TournamentManager()
        }
        return TournamentManager._instance
    }

    private init() {
        // Ascolta il segnale per aprire il torneo e richiedere dati aggiornati
        ;(GameSignals as any).openTournament?.connect(this.onOpenTournament.bind(this))

        // Ascolta il segnale per unirsi al torneo
        ;(GameSignals as any).joinTournament?.connect(this.onJoinTournament.bind(this))

        // Ascolta i dati torneo dal backend
        ;(GameSignals as any).tournamentDataReceived?.connect(this.onTournamentDataReceived.bind(this))

        // Ascolta l'init response per estrarre i dati torneo
        GameSignals.initResponse?.connect(this.onInitResponse.bind(this))

        // Badge torneo deve apparire solo dopo la transition reale intro -> base game.
        // completeSplashScreenState scatta al click splash (troppo presto), quindi usiamo
        // il segnale di transition completion e il fallback base background.
        GameSignals.completeTransitionAnimation?.connect(this.onBaseGameTransitionCompleted)
        GameSignals.showBaseGameBackground?.connect(this.onBaseGameTransitionCompleted)

        console.log('[TournamentManager] Initialized')
    }

    private onBaseGameTransitionCompleted = () => {
        if (this._baseGameTransitionCompleted) return

        this._baseGameTransitionCompleted = true
        console.log('[TournamentManager] Base game transition completed')

        if (this._pendingBadgeAfterTransition) {
            this._pendingBadgeAfterTransition = false
            this.showBadgeOnly()
        }
    }

    private onOpenTournament() {
        console.log('[TournamentManager] Opening tournament')
        
        const model = (Facade as any).model
        const tournamentData = model?.tournamentData || model?.initData?.tournament_data

        if (!tournamentData) {
            console.warn('[TournamentManager] No tournament data available')
            return
        }

        this._tournamentId = tournamentData.tournament_id

        const isPlayerJoined = Number(tournamentData?.is_player_joined || 0) === 1

        // Legacy behavior: first automatic open after splash should show only the trophy badge
        // when the player already joined the tournament.
        if (isPlayerJoined && !this._autoOpenHandled) {
            this._autoOpenHandled = true

            if (!this._baseGameTransitionCompleted) {
                this._pendingBadgeAfterTransition = true
                ;(Facade as any)._showTournament = false
                ;(Facade as any)._showTournamentBadge = false
                return
            }

            this.showBadgeOnly()
            return
        }

        this._autoOpenHandled = true

        // Do not call tournament PHP on popup open.
        // Backend request must happen only when user clicks Join.

        // Apri il popup del torneo
        this.open()
    }

    private showBadgeOnly() {
        console.log('[TournamentManager] Showing tournament badge only')

        if (!this._baseGameTransitionCompleted) {
            this._pendingBadgeAfterTransition = true
            ;(Facade as any)._showTournament = false
            ;(Facade as any)._showTournamentBadge = false
            return
        }

        ;(Facade as any)._showTournament = false
        ;(Facade as any)._showTournamentBadge = true

        const adapter = (window as any).__cashCongExternalUiAdapter
        if (adapter && typeof adapter.pushSnapshot === 'function') {
            adapter.pushSnapshot()
        }
    }

    private open() {
        console.log('[TournamentManager] Setting showTournament to true')
        ;(Facade as any)._showTournament = true
        ;(Facade as any)._showTournamentBadge = false
        
        // Aggiorna lo snapshot per notificare la UI
        const adapter = (window as any).__cashCongExternalUiAdapter
        if (adapter && typeof adapter.pushSnapshot === 'function') {
            adapter.pushSnapshot()
        }
    }

    public openFromBadge() {
        // Explicit user action from badge must always open popup.
        this._autoOpenHandled = true
        this.open()
    }

    public close() {
        console.log('[TournamentManager] Setting showTournament to false')
        ;(Facade as any)._showTournament = false

        const model = (Facade as any).model
        const tournamentData = model?.tournamentData || model?.initData?.tournament_data
        const isPlayerJoined = Number(tournamentData?.is_player_joined || 0) === 1

        if (isPlayerJoined) {
            if (this._baseGameTransitionCompleted) {
                ;(Facade as any)._showTournamentBadge = true
            } else {
                this._pendingBadgeAfterTransition = true
                ;(Facade as any)._showTournamentBadge = false
            }
        } else {
            this._pendingBadgeAfterTransition = false
            ;(Facade as any)._showTournamentBadge = false
        }
        
        // Aggiorna lo snapshot per notificare la UI
        const adapter = (window as any).__cashCongExternalUiAdapter
        if (adapter && typeof adapter.pushSnapshot === 'function') {
            adapter.pushSnapshot()
        }
    }

    private onInitResponse(data: any) {
        console.log('[TournamentManager] Init response received', data)

        // Estrai e salva i dati torneo se presenti
        if (data?.tournament_data) {
            const model = (Facade as any).model
            if (model) {
                model.tournamentData = data.tournament_data
                this._tournamentId = data.tournament_data.tournament_id
            }

            const isPlayerJoined = Number(data.tournament_data?.is_player_joined || 0) === 1
            if (isPlayerJoined) {
                if (this._baseGameTransitionCompleted) {
                    ;(Facade as any)._showTournamentBadge = true
                } else {
                    this._pendingBadgeAfterTransition = true
                    ;(Facade as any)._showTournamentBadge = false
                }
            } else {
                this._pendingBadgeAfterTransition = false
                ;(Facade as any)._showTournamentBadge = false
            }

            console.log('[TournamentManager] Tournament data found:', data.tournament_data)

            // Trigger snapshot update nell'ExternalUiAdapter
            ;(GameSignals as any).backendMessage?.emit({ action: 'tournament_data_updated' })

            console.log('[TournamentManager] Tournament auto-open is delegated to splash transition completion')
        }
    }

    private onTournamentDataReceived(data: any) {
        console.log('[TournamentManager] Tournament data updated:', data)

        // Aggiorna il model
        const model = (Facade as any).model
        if (model && data) {
            // Merge i nuovi dati con quelli esistenti
            model.tournamentData = {
                ...model.tournamentData,
                ...data,
            }
        }

        // Trigger snapshot update
        ;(GameSignals as any).backendMessage?.emit({ action: 'tournament_data_updated' })
    }

    private onJoinTournament(tournamentId: number) {
        const model = (Facade as any).model
        const fallbackTournamentId = this._tournamentId || model?.tournamentData?.tournament_id || model?.initData?.tournament_data?.tournament_id
        const resolvedTournamentId = Number(tournamentId || fallbackTournamentId || 0)

        console.log('[TournamentManager] Joining tournament:', {
            requestedTournamentId: tournamentId,
            fallbackTournamentId,
            resolvedTournamentId,
        })

        if (!resolvedTournamentId) {
            console.warn('[TournamentManager] Join skipped: missing tournament id')
            return
        }

        if (this._baseGameTransitionCompleted) {
            ;(Facade as any)._showTournamentBadge = true
        } else {
            this._pendingBadgeAfterTransition = true
            ;(Facade as any)._showTournamentBadge = false
        }
        
        const backendManager = (Facade as any).backendManager
        if (backendManager && typeof backendManager.joinTournament === 'function') {
            backendManager.joinTournament(resolvedTournamentId)
        }
    }

    private requestLeaderboard(tournamentId: number) {
        console.log('[TournamentManager] Requesting leaderboard for tournament:', tournamentId)
        
        const backendManager = (Facade as any).backendManager
        if (backendManager && typeof backendManager.getTournamentLeaderboard === 'function') {
            backendManager.getTournamentLeaderboard(tournamentId)
        }
    }

    public refreshLeaderboard() {
        if (this._tournamentId) {
            this.requestLeaderboard(this._tournamentId)
        }
    }
}

// Inizializza il singleton
TournamentManager.instance

// Esponi metodo globale per testing da console
;(window as any).openTournament = () => {
    console.log('[Global] Opening tournament from console command')
    ;(GameSignals as any).openTournament?.emit()
}

;(window as any).closeTournament = () => {
    console.log('[Global] Closing tournament from console command')
    TournamentManager.instance.close()
}
